import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useClientStore } from "../../store/useClientStore";
import { useBidStore } from "../../store/useBidStore";
import ClientCard from "./ClientCard";
import LoaderSpinner from "../ui/LoaderSpinner";

const ClientList = () => {
  const { clients, isFetchingClients } = useClientStore();
  const { bids, fetchMyBids } = useBidStore();
  const [q, setQ] = useState("");

  useEffect(() => {
    fetchMyBids();
  }, [fetchMyBids]);

  // Client-side search across the key contact fields.
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c: any) =>
      [c.name, c.username, c.email, c.phone, c.company].filter(Boolean).join(" ").toLowerCase().includes(s)
    );
  }, [clients, q]);

  // Bids currently in Negotiation, grouped by their linked contact.
  const negotiatingByContact = useMemo(() => {
    const map: Record<string, any[]> = {};
    bids.forEach((b: any) => {
      if (b.bidStatus !== "In Negotiation" || !b.contactRef) return;
      const id = String(b.contactRef._id || b.contactRef);
      (map[id] = map[id] || []).push(b);
    });
    return map;
  }, [bids]);

  // Mirror scrollbar on top of the table, synced with the body scroll.
  // NOTE: these hooks must run before any early return (Rules of Hooks).
  const topRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scrollW, setScrollW] = useState(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const update = () => setScrollW(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [filtered]);
  const syncFromTop = () => { if (bodyRef.current && topRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft; };
  const syncFromBody = () => { if (bodyRef.current && topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft; };

  if (isFetchingClients) {
    return (
      <div className="flex justify-center items-center py-16">
        <LoaderSpinner size={40} />
      </div>
    );
  }

  if (!clients.length) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-xl">
        <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 mx-auto mb-4"></div>
        <p className="text-gray-500 font-medium">No contacts found.</p>
        <p className="text-sm text-gray-400 mt-1">Start by adding a new client above.</p>
      </div>
    );
  }

  const th = "px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap";

  return (
    <div className="space-y-4">
      {/* Search (matches the Projects list) */}
      <div className="relative">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search contacts by name, email, company…"
          className="w-full pl-11 pr-3 py-2.5 rounded-lg bg-surface-container-low border border-outline-variant text-sm outline-none focus:border-primary"
        />
      </div>

      <div className="grid gap-4 md:hidden">
        {filtered.map((client) => (
          <ClientCard key={client._id} client={client} negotiatingBids={negotiatingByContact[client._id] || []} />
        ))}
      </div>

      {/* Top horizontal scrollbar (mirrors the table's) */}
      <div ref={topRef} onScroll={syncFromTop} className="hidden md:block overflow-x-auto cre-scroll">
        <div style={{ width: scrollW, height: 1 }} />
      </div>

      <div ref={bodyRef} onScroll={syncFromBody} className="hidden md:block max-h-[620px] overflow-auto cre-scroll rounded-2xl border border-gray-100 bg-white shadow-sm">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gradient-to-r from-[#2462b2]/5 to-[#1e55a0]/5 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              {["Name", "Username", "Email", "Phone", "Tier", "Type", "Created", "Source", "Status", "Notes", "Website", "Negotiating", "Company", "Location", "Time Zone", "Actions"].map((h) => (
                <th key={h} className={`${th} ${h === "Negotiating" ? "!px-2 w-16 text-center" : ""}`}>{h === "Negotiating" ? "Neg." : h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((client) => (
              <ClientCard key={client._id} client={client} negotiatingBids={negotiatingByContact[client._id] || []} />
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={16} className="text-center py-10 text-gray-400 text-sm">No contacts match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientList;
